'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from '@/lib/tokens';
import { buildRealtimeSystemPrompt } from '@/lib/promptBuilder';
import { getRecordingEnvFromHostname } from '@/lib/environment';
import {
  useTextRealtimeSession,
  type TextTranscriptEntry,
} from '@/hooks/useTextRealtimeSession';
import type { ConversationAnalysis } from '@/lib/textTestingAnalysis';
import { buildMainQuestionLabelByTurnId } from '@/lib/textTestingAnalysis';
import type { PilotStudy } from '@/lib/studies/types';

type TestMode = 'manual' | 'automatic';

const STYLE_PRESETS: { label: string; value: string }[] = [
  {
    label: 'Brief',
    value:
      'Give short, somewhat terse answers (1–3 sentences). Answer the question directly without much elaboration unless asked.',
  },
  {
    label: 'Detailed',
    value:
      'Give thoughtful, detailed answers with concrete examples from personal experience. Speak naturally in a few sentences to a short paragraph.',
  },
  {
    label: 'Evasive',
    value:
      'Be polite but vague. Often dodge specifics, say you are not sure, or give incomplete answers that invite follow-up.',
  },
  {
    label: 'Emotional',
    value:
      'Share personally and with feeling. Mention how AI companionship affects your mood, loneliness, or relationships.',
  },
];

const DEFAULT_STYLE = STYLE_PRESETS[1].value;

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    idle: { bg: T.creamMid, text: T.espressoLight },
    connecting: { bg: 'oklch(92% 0.04 65)', text: 'oklch(48% 0.14 45)' },
    ready: { bg: 'oklch(90% 0.04 152)', text: 'oklch(38% 0.08 152)' },
    error: { bg: 'oklch(93% 0.04 20)', text: 'oklch(38% 0.12 20)' },
    ended: { bg: T.creamMid, text: T.espressoMid },
  };
  const c = colors[status] ?? colors.idle;
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        borderRadius: 999,
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  );
}

function TranscriptBubble({
  entry,
  isStreaming,
  questionLabel,
}: {
  entry: TextTranscriptEntry;
  isStreaming: boolean;
  questionLabel?: string;
}) {
  const isAi = entry.role === 'assistant';
  return (
    <div
      className="fadeIn"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        alignItems: isAi ? 'flex-start' : 'flex-end',
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: isAi ? T.terra : T.espressoLight,
          paddingLeft: 4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {isAi ? 'AI' : 'You'}
        {questionLabel ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: T.white,
              background: T.terra,
              borderRadius: 4,
              padding: '1px 6px',
            }}
          >
            {questionLabel}
          </span>
        ) : null}
      </span>
      <div
        style={{
          maxWidth: '85%',
          background: isAi ? T.terraLight : T.creamMid,
          borderRadius: isAi ? '4px 14px 14px 14px' : '14px 14px 4px 14px',
          padding: '10px 14px',
          fontSize: 14,
          lineHeight: 1.55,
          color: T.espresso,
          fontStyle: isStreaming ? 'italic' : 'normal',
          whiteSpace: 'pre-wrap',
        }}
      >
        {entry.text}
        {isStreaming && (
          <span
            style={{
              display: 'inline-block',
              width: 2,
              height: 14,
              background: T.terra,
              marginLeft: 3,
              verticalAlign: 'middle',
              animation: 'pulse 1s ease-in-out infinite',
            }}
          />
        )}
      </div>
    </div>
  );
}

function AnalysisPanel({ analysis }: { analysis: ConversationAnalysis }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '16px 18px',
        borderRadius: 12,
        border: `1px solid ${T.creamBorder}`,
        background: T.white,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: T.terra,
        }}
      >
        Analysis
        <span style={{ fontWeight: 500, color: T.espressoLight, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>
          interviewing phase only
        </span>
      </span>
      <div style={{ fontSize: 13, color: T.espresso, lineHeight: 1.55 }}>
        <div>
          Turns: <strong>{analysis.turns.total}</strong>
          {' '}(AI {analysis.turns.assistant} / User {analysis.turns.user})
          <span style={{ color: T.espressoLight, fontSize: 12 }}>
            {' '}· consecutive same-speaker bubbles merged
          </span>
        </div>
        <div>
          Avg words/turn: AI {analysis.avgWords.assistant} · User {analysis.avgWords.user}
        </div>
        <div style={{ color: T.espressoLight, fontSize: 12 }}>
          Avg chars/turn (no spaces): AI {analysis.avgChars.assistant} · User {analysis.avgChars.user}
        </div>
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: T.espressoMid,
            marginBottom: 8,
          }}
        >
          Probing follow-ups per main question
        </div>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {analysis.mainQuestions.map((q) => (
            <li key={q.index} style={{ fontSize: 13, color: T.espresso, lineHeight: 1.45 }}>
              <span style={{ color: T.espressoLight }}>Q{q.index}: </span>
              <span style={{ color: T.espressoMid }}>
                {q.question.length > 110 ? `${q.question.slice(0, 110)}…` : q.question}
              </span>
              <div style={{ marginTop: 2, fontWeight: 600 }}>
                {q.probingFollowups} probing follow-up{q.probingFollowups === 1 ? '' : 's'}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

type Props = { study: PilotStudy };

export function StudyTextTestingClient({ study }: Props) {
  const instructions = buildRealtimeSystemPrompt(study.config);
  const [mode, setMode] = useState<TestMode>('manual');
  const [userInput, setUserInput] = useState('');
  const [userStyle, setUserStyle] = useState(DEFAULT_STYLE);

  const session = useTextRealtimeSession({
    instructions,
    closingMessage: study.config.closingMessage,
    studyId: study.id,
    mode,
    userStyle,
  });
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    status,
    sessionId,
    transcript,
    streamingText,
    ended,
    error,
    awaitingUser,
    start,
    stop,
    sendText,
  } = session;
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ConversationAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoRunningRef = useRef(false);
  const autoBusyRef = useRef(false);
  const lastSimulatedAssistantIdRef = useRef<string | null>(null);
  const analysisSessionRef = useRef<string | null>(null);

  const shareUrl = useMemo(() => {
    if (!sessionId || typeof window === 'undefined') return null;
    const env = getRecordingEnvFromHostname(window.location.hostname);
    return `${window.location.origin}/share/text-testing/${env}/${study.id}/${sessionId}`;
  }, [sessionId, study.id]);

  const qLabelByTurnId = useMemo(
    () => buildMainQuestionLabelByTurnId(analysis),
    [analysis],
  );

  const handleCopyShareLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      if (shareCopiedTimerRef.current) clearTimeout(shareCopiedTimerRef.current);
      shareCopiedTimerRef.current = setTimeout(() => setShareCopied(false), 2000);
    } catch {
      setShareCopied(false);
    }
  }, [shareUrl]);

  useEffect(() => {
    return () => {
      if (shareCopiedTimerRef.current) clearTimeout(shareCopiedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    autoRunningRef.current = autoRunning;
  }, [autoRunning]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, streamingText, analysis, analyzing]);

  useEffect(() => {
    if (status === 'ready' && mode === 'manual') {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [status, mode]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const handleConnect = useCallback(() => {
    setAutoError(null);
    setAnalysis(null);
    setAnalysisError(null);
    analysisSessionRef.current = null;
    lastSimulatedAssistantIdRef.current = null;
    void start();
  }, [start]);

  const handleDisconnect = useCallback(() => {
    setAutoRunning(false);
    autoRunningRef.current = false;
    setAutoBusy(false);
    autoBusyRef.current = false;
    stop();
  }, [stop]);

  const handleSend = useCallback(() => {
    const text = userInput.trim();
    if (!text || status !== 'ready' || ended) return;
    setUserInput('');
    sendText(text);
  }, [userInput, status, ended, sendText]);

  const runSimulationTurn = useCallback(async () => {
    if (autoBusyRef.current || ended || status !== 'ready') return;
    const last = transcript[transcript.length - 1];
    if (!last || last.role !== 'assistant') return;
    if (lastSimulatedAssistantIdRef.current === last.id) return;

    autoBusyRef.current = true;
    setAutoBusy(true);
    setAutoError(null);
    // Claim this assistant turn only for the duration of this attempt. Mark as
    // permanently handled only after sendText succeeds — otherwise a failed or
    // aborted send leaves automatic mode stuck on "Waiting for simulated user…".
    const claimedId = last.id;
    lastSimulatedAssistantIdRef.current = claimedId;

    try {
      const resp = await fetch('/api/interview/simulate-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: transcript.map((t) => ({
            role: t.role,
            content: t.text,
          })),
          style: userStyle,
          studyTitle: study.title,
        }),
      });
      const data = (await resp.json()) as { text?: string; error?: string };
      if (!resp.ok || !data.text) {
        throw new Error(data.error ?? 'Simulation failed');
      }
      if (!autoRunningRef.current || ended) {
        if (lastSimulatedAssistantIdRef.current === claimedId) {
          lastSimulatedAssistantIdRef.current = null;
        }
        return;
      }
      const sent = sendText(data.text);
      if (!sent) {
        if (lastSimulatedAssistantIdRef.current === claimedId) {
          lastSimulatedAssistantIdRef.current = null;
        }
        setAutoError('Could not send simulated reply (connection not ready). Retrying…');
      }
    } catch (err) {
      if (lastSimulatedAssistantIdRef.current === claimedId) {
        lastSimulatedAssistantIdRef.current = null;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setAutoError(msg);
      setAutoRunning(false);
      autoRunningRef.current = false;
    } finally {
      autoBusyRef.current = false;
      setAutoBusy(false);
    }
  }, [ended, status, transcript, userStyle, study.title, sendText]);

  // Automatic loop: when AI turn completes, simulate the next user reply.
  useEffect(() => {
    if (mode !== 'automatic') return;
    if (!autoRunning) return;
    if (!awaitingUser || ended || status !== 'ready') return;
    if (autoBusy) return;
    void runSimulationTurn();
  }, [mode, autoRunning, awaitingUser, ended, status, autoBusy, runSimulationTurn]);

  useEffect(() => {
    if (ended) {
      setAutoRunning(false);
      autoRunningRef.current = false;
    }
  }, [ended]);

  // Run conversation analysis once when the interview ends.
  useEffect(() => {
    if (!ended || !sessionId || transcript.length === 0) return;
    if (analysisSessionRef.current === sessionId) return;
    analysisSessionRef.current = sessionId;

    let cancelled = false;
    setAnalyzing(true);
    setAnalysisError(null);

    void (async () => {
      try {
        const resp = await fetch('/api/interview/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transcript: transcript.map((t) => ({
              id: t.id,
              role: t.role,
              text: t.text,
              phase: t.phase,
            })),
            mainQuestions: study.config.interviewQuestions,
            sessionId,
            studyId: study.id,
          }),
        });
        const data = (await resp.json()) as {
          analysis?: ConversationAnalysis;
          error?: string;
        };
        if (!resp.ok || !data.analysis) {
          throw new Error(data.error ?? 'Analysis failed');
        }
        if (!cancelled) setAnalysis(data.analysis);
      } catch (err) {
        if (!cancelled) {
          setAnalysisError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ended, sessionId, transcript, study.config.interviewQuestions, study.id]);

  const isConnected = status === 'ready';
  const canSend =
    mode === 'manual' && isConnected && !ended && userInput.trim().length > 0;

  const stateLabel = ended
    ? 'Interview complete'
    : streamingText
      ? 'AI responding…'
      : autoBusy
        ? 'Simulating user…'
        : awaitingUser
          ? mode === 'automatic'
            ? 'Waiting for simulated user…'
            : 'Your turn'
          : status === 'connecting'
            ? 'Connecting…'
            : 'Idle';

  return (
    <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div
        style={{
          borderBottom: `1px solid ${T.creamBorder}`,
          padding: '14px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: T.white,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: T.terra,
                boxShadow: '0 0 0 3px oklch(62% 0.13 48 / 0.12)',
              }}
            />
            <span className="serif" style={{ fontSize: 17, color: T.espresso, letterSpacing: '-0.01em' }}>
              AI Interviewer
            </span>
          </div>
          <span style={{ color: T.creamBorder, fontSize: 18 }}>—</span>
          <span
            style={{
              fontSize: 14,
              color: T.espressoMid,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {study.title} · Text testing
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: T.terra,
            border: `1.5px solid ${T.terra}`,
            borderRadius: 4,
            padding: '2px 7px',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          DEV
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left panel */}
        <div
          style={{
            width: 300,
            flexShrink: 0,
            borderRight: `1px solid ${T.creamBorder}`,
            display: 'flex',
            flexDirection: 'column',
            padding: '28px 22px',
            gap: 24,
            background: T.white,
            overflowY: 'auto',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: T.espressoLight,
                fontWeight: 500,
                marginBottom: 8,
              }}
            >
              {stateLabel}
            </div>
            <p style={{ fontSize: 12, color: T.espressoLight, lineHeight: 1.45, margin: 0 }}>
              Realtime text-only path. Separate from the audio interview session.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.espressoLight,
              }}
            >
              Status
            </span>
            <StatusBadge status={ended ? 'ended' : status} />
            {error && (
              <p style={{ fontSize: 12, color: T.danger, lineHeight: 1.4, margin: 0 }}>{error}</p>
            )}
            {autoError && (
              <p style={{ fontSize: 12, color: T.danger, lineHeight: 1.4, margin: 0 }}>{autoError}</p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.espressoLight,
              }}
            >
              Mode
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['manual', 'automatic'] as const).map((m) => {
                const active = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={autoRunning}
                    onClick={() => {
                      setMode(m);
                      setAutoRunning(false);
                      autoRunningRef.current = false;
                    }}
                    style={{
                      flex: 1,
                      border: `1.5px solid ${active ? T.terra : T.creamBorder}`,
                      background: active ? T.terraLight : T.white,
                      color: active ? T.terraDeep : T.espressoMid,
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: autoRunning ? 'default' : 'pointer',
                      fontFamily: 'inherit',
                      textTransform: 'capitalize',
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          {mode === 'automatic' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: T.espressoLight,
                }}
              >
                Simulated user style
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {STYLE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    disabled={autoRunning}
                    onClick={() => setUserStyle(p.value)}
                    style={{
                      border: `1px solid ${userStyle === p.value ? T.terra : T.creamBorder}`,
                      background: userStyle === p.value ? T.terraLight : T.white,
                      color: T.espressoMid,
                      borderRadius: 6,
                      padding: '4px 8px',
                      fontSize: 11,
                      cursor: autoRunning ? 'default' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <textarea
                value={userStyle}
                onChange={(e) => setUserStyle(e.target.value)}
                disabled={autoRunning}
                rows={5}
                style={{
                  border: `1.5px solid ${T.creamBorder}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: T.espresso,
                  background: autoRunning ? T.creamMid : T.white,
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
              {isConnected && !ended && (
                <button
                  type="button"
                  onClick={() => {
                    if (autoRunning) {
                      setAutoRunning(false);
                      autoRunningRef.current = false;
                    } else {
                      setAutoError(null);
                      // Allow retrying the current assistant turn if a prior
                      // simulate attempt claimed it but never sent a reply.
                      lastSimulatedAssistantIdRef.current = null;
                      setAutoRunning(true);
                      autoRunningRef.current = true;
                    }
                  }}
                  style={{
                    background: autoRunning ? T.creamMid : T.terra,
                    color: autoRunning ? T.espressoMid : T.white,
                    border: autoRunning ? `1px solid ${T.creamBorder}` : 'none',
                    borderRadius: 10,
                    padding: '10px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {autoRunning ? (autoBusy ? 'Simulating… (Stop)' : 'Stop auto') : 'Start auto'}
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 11, color: T.espressoLight, margin: 0, lineHeight: 1.4 }}>
              Study ID: {study.id}
            </p>
            {sessionId && shareUrl && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${T.creamBorder}`,
                  background: T.white,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: T.espressoMid,
                  }}
                >
                  Shareable link
                </span>
                <p
                  style={{
                    fontSize: 10,
                    color: T.espressoLight,
                    margin: 0,
                    lineHeight: 1.4,
                    wordBreak: 'break-all',
                  }}
                >
                  {shareUrl}
                </p>
                <button
                  type="button"
                  onClick={() => void handleCopyShareLink()}
                  style={{
                    alignSelf: 'flex-start',
                    background: shareCopied ? T.sageLight : T.creamMid,
                    color: shareCopied ? 'oklch(38% 0.08 152)' : T.espressoMid,
                    border: `1px solid ${T.creamBorder}`,
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {shareCopied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            )}
            {!isConnected ? (
              <button
                type="button"
                onClick={handleConnect}
                disabled={status === 'connecting'}
                style={{
                  background: status === 'connecting' ? T.creamMid : T.terra,
                  color: status === 'connecting' ? T.espressoLight : T.white,
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: status === 'connecting' ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  width: '100%',
                }}
              >
                {status === 'connecting' ? 'Connecting…' : 'Connect'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDisconnect}
                style={{
                  background: 'transparent',
                  color: T.espressoMid,
                  border: `1px solid ${T.creamBorder}`,
                  borderRadius: 10,
                  padding: '12px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  width: '100%',
                }}
              >
                Disconnect
              </button>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <div
            style={{
              padding: '16px 24px',
              borderBottom: `1px solid ${T.creamBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: T.espresso }}>Conversation</span>
            {transcript.length > 0 && (
              <span style={{ fontSize: 11, color: T.espressoLight }}>
                {transcript.length} turn{transcript.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {transcript.length === 0 && !streamingText ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ fontSize: 14, color: T.espressoLight, textAlign: 'center' }}>
                  {isConnected
                    ? 'The AI will start shortly…'
                    : 'Connect to start the text interview'}
                </p>
              </div>
            ) : (
              transcript.map((entry) => (
                <TranscriptBubble
                  key={entry.id}
                  entry={entry}
                  isStreaming={false}
                  questionLabel={qLabelByTurnId.get(entry.id)}
                />
              ))
            )}
            {streamingText && (
              <TranscriptBubble
                entry={{
                  id: 'streaming',
                  role: 'assistant',
                  text: streamingText,
                  timestamp: '',
                  phase: 'interviewing',
                }}
                isStreaming
              />
            )}
            {ended && (
              <div style={{ marginTop: 8 }}>
                {analyzing && (
                  <p style={{ fontSize: 13, color: T.espressoLight, margin: 0 }}>
                    Analyzing conversation…
                  </p>
                )}
                {analysisError && (
                  <p style={{ fontSize: 13, color: T.danger, margin: 0, lineHeight: 1.45 }}>
                    Analysis failed: {analysisError}
                  </p>
                )}
                {analysis && <AnalysisPanel analysis={analysis} />}
              </div>
            )}
            <div ref={transcriptEndRef} />
          </div>

          <div
            style={{
              borderTop: `1px solid ${T.creamBorder}`,
              padding: '16px 24px',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-end',
            }}
          >
            {mode === 'manual' ? (
              <>
                <textarea
                  ref={textareaRef}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    isConnected
                      ? 'Type as the respondent… (Enter to send)'
                      : 'Connect first to send a message'
                  }
                  disabled={!isConnected || ended}
                  rows={2}
                  style={{
                    flex: 1,
                    border: `1.5px solid ${T.creamBorder}`,
                    borderRadius: 12,
                    padding: '10px 14px',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: T.espresso,
                    background: isConnected && !ended ? T.white : T.creamMid,
                    resize: 'none',
                    outline: 'none',
                    opacity: isConnected && !ended ? 1 : 0.5,
                  }}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend}
                  style={{
                    background: canSend ? T.terra : T.creamMid,
                    color: canSend ? T.white : T.espressoLight,
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 18px',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: canSend ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                    flexShrink: 0,
                    height: 42,
                  }}
                >
                  Send
                </button>
              </>
            ) : (
              <p style={{ fontSize: 13, color: T.espressoLight, margin: 0, lineHeight: 1.45 }}>
                {ended
                  ? 'Interview ended.'
                  : autoRunning
                    ? 'Automatic mode is running — simulated replies are sent when the AI finishes a turn.'
                    : isConnected
                      ? 'Press Start auto to simulate the respondent from the style above.'
                      : 'Connect, then start automatic simulation.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
